/**
 * @file: 聊天集合
 * @author: wangzhenhai02@baidu.com
 * @Date: 2022-06-23 11:36:48
 * @LastEditors: chenyingliao <chenyingliao@baidu.com>
 * @LastEditTime: 2024-03-21 19:32:09
 * @FilePath: /console-znzs/src/utils/chat.js
 */
import store from '@/znzs/src/vuex';
import {queryManualWords, getTicketType} from '@/znzs/src/api/robot.js';
import {removeTinymceImgWidthHeight, transformTicketParams, beforeSend, user} from '@/znzs/src/utils/index';
import {constant, CHANNEL, DIALOG_TYPE, DISPLAY_TYPE} from '@/znzs/src/utils/enum';
import chatHook from './chatHook';

export const createDialogId = () => Date.now() + Math.random().toString(16).slice(2);

/**
 * @description: 用户发送之后在聊天框展示内容
 * @param {*} list包含展示所需要的信息
 */
export const handleAfterUserAction = data => {
    if (data.dialogInfo && data.dialogInfo.content) {
        data.dialogInfo.content = removeTinymceImgWidthHeight(data.dialogInfo.content);
    }
    data.dialogId = data.dialogId ? data.dialogId : createDialogId();
    store.commit('setChatRecordsList', [data]);
    return data.dialogId;
};

/**
 * 将消息推送到页面显示并缓存下来
 *
 * @param dialogType 消息类型
 * @param display 显示信息，默认值为 dialogType
 * @param content 消息内容，默认为空字符串
 * @param extendInfo 扩展信息
 * @returns 处理后的消息
 */
export const pushMessage = (dialogType, display = dialogType, content = '', extendInfo = {}) => {
    const message = {
        dialogTime: Date.now(),
        dialogType,
    };
    if (display.display) {
        message.dialogInfo = display;
    }
    else {
        const dialogInfo = {
            ...extendInfo,
            display,
        };
        if (typeof content !== 'string') {
            dialogInfo.file = content;
        }
        else {
            dialogInfo.content = content;
        }
        message.dialogInfo = dialogInfo;
    }
    return handleAfterUserAction(message);
};



/**
  * 点击发送消息之后的逻辑处理
  * @param {*} content  发送的内容
  * @param {*} type  发送内容类型
  * @param {*} extendInfo  扩展信息，包括productId,images等
  */
export const handleAfterClick = (content, type, extendInfo) => {
    if (beforeSend(store)) {
        return;
    }
    pushMessage(DIALOG_TYPE.USER_QUESTION, DISPLAY_TYPE.TEXT, content, extendInfo);
    if (!store.state.chat.socket) {
        // 获取机器人回复
        getRobotReplay(content, type, extendInfo);
    }
    else {
        // 发送IM消息
        store.dispatch('chat/send', {content});
    }
};

/**
 * @description: 将图片/文件发送到后端处理
 * @param {*} params
 */
export function sendFileToService(content, type, file) {
    if (beforeSend(store)) {
        return;
    }
    pushMessage(DIALOG_TYPE.USER_QUESTION, {
        content,
        file, // 将file对象发送到dialogInfo中，是为了方便触发提交工单卡片的时候获取
        display: content ? DISPLAY_TYPE.HTML : DISPLAY_TYPE.FILE,
    });
    if (!store.state.chat.socket) {
        // 获取机器人回复
        getRobotReplay(content, type);
    }
    else {
        // 发送IM消息
        store.dispatch('chat/send', {content, file});
    }
}

function addLoadingTimer(fn, delay) {
    return async function getRobotReplay(...args) {
        let loadingId = null;
        // 超时添加loading卡片
        const timer = setTimeout(() => {
            loadingId = pushMessage(DIALOG_TYPE.WELCOME, DISPLAY_TYPE.ERNIE_LOADING);
        }, delay);
        await chatHook.sendBefore();
        await fn(...args);
        if (loadingId) {
            store.commit('removeChatRecord', loadingId);
        }
        else {
            clearTimeout(timer);
        }
    };
}

/**
 * 根据传入参数获取机器人的基本回复的底层方法
 * 不会添加loading效果
 *
 * @param content - 用户发送的消息内容
 * @param type - 触发机器人的消息类型
 * @param productId - 产品Id
 * @returns 机器人的回复
 */
export async function basicGetRobotReply(content, type, extendInfo = {}) {
    const params = {
        trigger: type,
        channel: CHANNEL,
        content,
        userName: user.name,
        source: window.location.href, // 添加来源信息(url)
        ...extendInfo,
    };
    // 说明：用户发送消息之后，机器人返回消息的展示
    try {
        await chatHook.requestInstance(params);
    }
    catch (e) {
        console.error(e);
    }
}

/**
 * 获取机器人响应的接口，添加0.5s的延迟loading
 */
const getRobotReplyInner = addLoadingTimer(basicGetRobotReply, 500);


/**
 * 非人工模式下获取机器人回复
 * @param {*} content
 * @param {*} type
 * @param {*} productId
 */
export function getRobotReplay(...args) {
    getRobotReplyInner(...args);
}

/**
 * @deprecated
 * @description: 如果转人工失败，获取转工单的话术和跳转链接
 */
export async function getTurnToTicketWords() {
    try {
        const response = await queryManualWords();
        pushMessage(DISPLAY_TYPE.TO_SUBMIT_TICKET, DISPLAY_TYPE.TO_SUBMIT_TICKET, response.data);
    }
    catch (e) {
        console.error(e);
    }
}

/**
 * 根据创建工单的链接获取最新的参数,包括sessionid
 * @param {*} href 工单链接
 * @param {*} useTicketId 是否使用提单路径的分类id
 * @returns
 */
export const getTicketInfo = async (href = constant.true.createTicketLink, useTicketId = true) => {
    try {
        let {data} = await getTicketType();

        const {url, params} = transformTicketParams(href);
        let {productId, questionId} = params;
        // 防止从链接中得到undefined的数据，因此保留下面的逻辑
        if (useTicketId) {
            productId = productId || data.productId;
            questionId = questionId || data.questionId;
        }
        const allParams = Object.assign(data, params, {
            productId,
            questionId,
            znzsSessionId: data.znzsSessionId,
        });
        return {
            url,
            params: allParams,
        };
    }
    catch {
        return {url: href, params: {}};
    }
};

/**
 * 获取工单类型productId，questionId，znzsSessionId
 * @returns
 */
export const getTicket = async isGetSessionIdOnly => {
    try {
        const res = await getTicketType();
        const {data: {productId, questionId, znzsSessionId}} = res;
        let params = '';
        if (!isGetSessionIdOnly && (productId || questionId)) {
            params += `&productId=${productId}&questionId=${questionId}&channel=2`;
        }
        if (znzsSessionId) {
            params += `&znzsSessionId=${znzsSessionId}`;
        }
        return params;
    }
    catch {
        return '';
    }
};

/**
 * 将PC端工单链接增加一个参数
 * @param {*} url
 */
export function urlTransform(url, param) {
    let newUrl = url;
    if (newUrl.endsWith('/')) {
        newUrl = newUrl.slice(0, -1);
    }
    newUrl += param;
    if (url.endsWith('create')) {
        return newUrl.replace(/&/, '?');
    }
    return newUrl;
}
